/**
 * @fileOverview root javascript entry point for the PHP MVC app - will eventually replace core.js
 */

const debug = false;
const disableLiveChat = false;
const disableSessionReplay = false;

global.$ = global.jQuery = require('jquery');       // load jQuery first for other dependent scripts
import { syncLoginState } from './src/js/auth';     // detect browser page caching after a login/logout and force reload if needed
import './src/components/bootstrap';
import '@fontsource-variable/nunito';
import '@fontsource/b612-mono';
import './src/css/my-font-awesome-v6.scss';
// these ckeditor5 styles are loaded even if the editor is not launched
import './src/components/ckeditor5/css/ckeditor-content.css';               // .ck-content base styles
import './src/components/ckeditor5/css/ckeditor-content-overrides.styl';    // .ck-content overrides
import './src/css/base.styl';                                               // base stylesheet, CSS variables, and utility classes
import './src/js/global-error-handler';
import { ready, loaded, isTouchDevice } from '@aamasri/web-components/src/js/dom-utils';
import './src/js/lazy-loader';      // stubs for dialog, growl with auto-lazy-loading
import './src/js/tracking';         // session tracking, i.e., Inspectlet, Google Analytics etc
import { webpSupport} from "@aamasri/dom-utils";
import { stripWebp } from '@aamasri/web-components/src/js/image';           // handle unsupported webp images
import { highlightSyntax } from './src/js/syntaxHighlighter';


// on document ready
ready().then(async function() {
    if (debug) console.debug('document ready');

    syncLoginState();

    // jQuery configuration
    window.jQuery.ajaxSetup({ cache: false });	// prevent caching on all jQuery ajax requests

    // check webp support
    webpSupport('alpha').then(msg => {
        window.browserInfo.webpSupport = true;
        if (debug) console.debug(msg);
    }).catch(errorMessage => {
        window.browserInfo.webpSupport = false;
        console.info(errorMessage);
        stripWebp();    // remove unsupported responsive webp images from srcset attribute
    });

    // legacy support for core.js
    window.browserInfo = { touchscreen: isTouchDevice() };    // legacy support for global browserInfo.touchscreen

    // legacy on doc ready initialization
    if (typeof window.onAjaxDocumentReady === 'function')
        window.onAjaxDocumentReady();
});




loaded().then(async () => {
    if (debug) console.debug('window loaded');

    // lazy load slideshow
    if (document.querySelector('.slideshow')) {
        if (debug) console.debug('detected .slideshow - lazy loading...');

        await import(/* webpackChunkName: "slideshow" */ './src/components/slideshow/slideshow').then(module => {
            window.Slideshow = module.default;
            Slideshow.init();   // instantiate slideshow skeletons
            if (debug) console.debug('Slideshow added to window object', typeof window.Slideshow);
        })
        .catch(error => { throw `loading slideshow failed with: ${error.message}`; });
    }


    highlightSyntax();  // code syntax highlighter


    // upgrade thumbnail images marked .upgradeMe to a higher resolution
    await import(/* webpackChunkName: "image" */ '@aamasri/web-components/src/js/image').then(image => {
        window.upgradeThumbnails = image.upgradeThumbnails;
        image.upgradeThumbnails();
    });


    // lazy load tooltips
    await import(/* webpackChunkName: "tooltip" */ './src/components/tooltip/tooltip').then(module => {
        if (debug) console.debug('tooltip component loaded', typeof module);
        window.tooltip = module;
    }).catch(error => { throw `loading tooltip failed with: ${error.message}`; });


    // lazy load ckeditor (if there's editable content on the page)
    if (document.querySelector('.ck-content.editable')) {
        if (debug) console.debug('.ck-content.editable region detected: lazy loading html editor');
        await import(/* webpackChunkName: "html-editor" */'./src/components/ckeditor5/index')
            .catch(error => { throw `loading html-editor failed with: ${error.message}`; });
    }

    // legacy on-window-loaded initialization
    if (typeof window.onAjaxWindowLoaded === 'function')
        window.onAjaxWindowLoaded();


    // initialized the file manager's dragDropListener() if there are elements capable of receiving files
    if ((typeof visitorId === 'number') && visitorId && document.querySelector('[data-upload-path]')) {
        await import(/* webpackChunkName: "filemanager" */ './src/js/filemanager').then(filemanager => {
                filemanager.dragDropListener();
                if (debug) console.debug('initialized filemanager.dragDropListener()', typeof filemanager.dragDropListener);
            })
            .catch(error => { throw `initializing filemanager.dragDropListener() failed with: ${error.message}`; });
    }


    // uiCheckbox style/interactivity support
    await import(/* webpackChunkName: "ui-checkbox" */ './src/components/checkbox/checkbox.js');


    // Session replay and user behavior analytics
    if (typeof disableSessionReplay === 'undefined' || !disableSessionReplay) {
        await import(/* webpackChunkName: "session-replay" */ './src/js/session-replay-hotjar').then((module) => {
            if (debug) console.debug('session-replay loaded', typeof module);
        }).catch(error => {
            throw `loading session-replay failed with: ${error.message}`;
        });
    }

    // Live Chat Loader
    if (typeof disableLiveChat === 'undefined' || !disableLiveChat) {
        await import(/* webpackChunkName: "live-chat" */ './src/js/live-chat').then((module) => {
            if (debug) console.debug('live-chat component loaded', typeof module);
        }).catch(error => {
            throw `loading live-chat failed with: ${error.message}`;
        });
    }
});